import { useState } from "react";
import {
    createFileRoute,
    Link,
    useSearch,
    redirect,
} from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, ref, InferType } from "yup";
import { Button } from "@heroui/react";
import TextInput from "@/components/inputs/TextInput";
import useRequest from "@/lib/request/useRequest";
import sentinelLogo from "@/assets/images/Sentinel_Horizontal_TM.png";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";

interface PasswordResetSearchParams {
    uid?: string;
    token?: string;
}

export const Route = createFileRoute("/(auth)/password/reset/confirm/")({
    component: PasswordResetConfirm,
    beforeLoad: async ({ location, search }) => {
        if (localStorage.getItem("oldNTime") !== "false") {
            throw redirect({
                to: "/ntimev1/$",
                params: { _splat: location.pathname },
                search,
            });
        }
    },
    validateSearch: (
        search: Record<string, unknown>,
    ): PasswordResetSearchParams => {
        return {
            uid: typeof search.uid === "string" ? search.uid : undefined,
            token: typeof search.token === "string" ? search.token : undefined,
        };
    },
});

const schema = object({
    new_password1: string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(
            /^.*(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_]).*$/,
            "Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character",
        ),
    new_password2: string()
        .required("Confirm password is required")
        .oneOf([ref("new_password1")], "Passwords must match"),
});

type FormData = InferType<typeof schema>;

function PasswordResetConfirm() {
    const { t } = useTranslation("messages");
    const search = useSearch({
        from: "/(auth)/password/reset/confirm/",
    }) as PasswordResetSearchParams;
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { axios } = useRequest();

    const form = useForm<FormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            new_password1: "",
            new_password2: "",
        },
    });

    const resetPasswordConfirmRequest = useMutation({
        mutationFn: (data: {
            uid: string;
            token: string;
            new_password1: string;
            new_password2: string;
        }) => {
            return axios({
                url: "api-auth/password/reset/confirm/",
                method: "post",
                data,
            });
        },
    });
    const onSubmit = async (data: FormData) => {
        try {
            await resetPasswordConfirmRequest.mutateAsync({
                uid: search.uid ?? "",
                token: search.token ?? "",
                new_password1: data.new_password1,
                new_password2: data.new_password2,
            });
            setIsSubmitted(true);
            setErrorMessage("");
        } catch (error) {
            const err = error as {
                response?: { data?: Record<string, unknown> };
            };

            if (err.response?.data?.token) {
                setTokenExpired(true);
            } else if (err.response?.data?.new_password2) {
                setErrorMessage(String(err.response.data.new_password2));
            } else {
                setErrorMessage(t("error.failed_to_reset_password_try_again"));
            }
        }
    };

    return (
        <div className="flex flex-col items-center h-screen w-screen justify-center p-4">
            <div className="w-full max-w-[25rem]">
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className="flex flex-col gap-4">
                            <img
                                src={sentinelLogo}
                                className="px-8 w-full max-w-md mx-auto"
                            />

                            {!isSubmitted ? (
                                !tokenExpired ? (
                                    <>
                                        <div className="text-center text-brandGreen-500 mt-4 mb-4">
                                            Enter your new password
                                        </div>

                                        <TextInput
                                            type="password"
                                            name="new_password1"
                                            label="New password"
                                            autoComplete="new-password"
                                        />

                                        <TextInput
                                            type="password"
                                            name="new_password2"
                                            label="Confirm new password"
                                            autoComplete="new-password"
                                        />

                                        {errorMessage && (
                                            <div className="text-red-500 text-sm mt-2">
                                                {errorMessage}
                                            </div>
                                        )}

                                        <div className="text-sm mt-2">
                                            <p>Password requirements:</p>
                                            <ul className="list-disc list-inside">
                                                <li>
                                                    Must be at least 8
                                                    characters
                                                </li>
                                                <li>
                                                    Must contain a special
                                                    character, uppercase letter,
                                                    and number
                                                </li>
                                            </ul>
                                        </div>

                                        <Button
                                            type="submit"
                                            color="primary"
                                            variant="solid"
                                            isLoading={
                                                resetPasswordConfirmRequest.isPending
                                            }
                                            className="mt-2"
                                        >
                                            Confirm
                                        </Button>
                                    </>
                                ) : (
                                    <div className="text-center mt-8">
                                        <h6 className="text-red-500 mb-2">
                                            This reset password page has expired
                                        </h6>
                                        <p className="mb-4">
                                            Please go back and request a new
                                            password reset email.
                                        </p>
                                        <Link to="/password/reset">
                                            <Button
                                                color="primary"
                                                variant="solid"
                                            >
                                                Back to Reset Password
                                            </Button>
                                        </Link>
                                    </div>
                                )
                            ) : (
                                <div className="text-center mt-8">
                                    <p className="mb-4">
                                        Password has been reset successfully!
                                    </p>
                                    <Link to="/auth">
                                        <Button color="primary" variant="solid">
                                            Back to Login
                                        </Button>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </form>
                </FormProvider>
            </div>
        </div>
    );
}

export default PasswordResetConfirm;
